/* Vaults · Mandatory contributions — each one has its own "Sweep now"; shows "Contribution made for this month" once paid. */
import { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, Check, AlertTriangle, CalendarClock, Loader2 } from "lucide-react";

export default function MandatorySweepCard() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState({});

  const load = () => api.get("/commitments/mandatory").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sweep = async (key) => {
    setBusy(key); setNotes((n) => ({ ...n, [key]: "" }));
    try {
      const { data: r } = await api.post("/commitments/sweep-now", { key });
      const hit = r.charged.includes(key) || (key.startsWith("baby:") && r.charged.some((c) => c === key));
      const err = r.errors?.[key] || (key.startsWith("baby:") ? (r.results.baby || []).find((b) => `baby:${b.vault_id}` === key)?.error : null);
      setNotes((n) => ({ ...n, [key]: hit ? "Contribution made for this month." : err ? `Couldn't sweep: ${err}` : "Already made for this month." }));
      await load(); refresh && refresh();
    } catch (e) { setNotes((n) => ({ ...n, [key]: formatErr(e.response?.data?.detail) || e.message })); }
    finally { setBusy(""); }
  };

  if (!data || data.items.length === 0) return null;
  return (
    <section className="mb-10 bg-linen border border-sand rounded-2xl p-6" data-testid="mandatory-sweep-card">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Mandatory contributions</div>
      <h2 className="font-serif text-2xl mt-1">This month&apos;s pulls.</h2>
      <p className="text-xs text-moss mt-1 max-w-lg">Each one leaves Spending on payday. Tap <strong>Sweep now</strong> to pull it immediately.</p>
      <ul className="mt-4 divide-y divide-sand-warm" data-testid="mandatory-list">
        {data.items.map((i) => {
          const tid = i.key.replace(":", "-");
          return (
            <li key={i.key} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`mandatory-item-${tid}`}>
              <div className="flex items-center gap-2 min-w-0">
                {i.paid ? <Check className="w-4 h-4 text-forest shrink-0" /> : <AlertTriangle className="w-4 h-4 text-gold shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{i.label} <span className="font-mono text-moss">· {fmtMoney(Number(i.amount || 0))}</span></div>
                  <div className={`text-[11px] ${i.paid ? "text-forest" : "text-sage"}`} data-testid={`mandatory-status-${tid}`}>
                    {i.paid ? "Contribution made for this month." : i.status === "not_yet_started" ? "Starts on your first payday." : i.error ? `Not yet — ${i.error}` : "Due this month."}
                  </div>
                  {notes[i.key] && <div className={`text-[11px] mt-0.5 ${notes[i.key].startsWith("Contribution") ? "text-forest" : "text-clay"}`} data-testid={`mandatory-note-${tid}`}>{notes[i.key]}</div>}
                </div>
              </div>
              <button onClick={() => sweep(i.key)} disabled={!!busy || i.paid}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${i.paid ? "bg-forest/10 text-forest cursor-default" : "bg-forest text-white hover:bg-forest-deep"} disabled:opacity-70`}
                data-testid={`mandatory-sweep-${tid}`}>
                {busy === i.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : i.paid ? <Check className="w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {i.paid ? "Made this month" : "Sweep now"}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
